import React, { useCallback, useEffect, useMemo } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import UltimaActualizacion from '../../../../atoms/support/UltimaActualizacion/UltimaActualizacion';
import { Space, Table } from 'antd';
import Acciones from '../../../../atoms/support/acciones/Acciones';
import TiempoPedido from '../../../../atoms/support/TiempoPedido/TiempoPedido';
import Local from '../../../../atoms/support/Local';
import DatosCliente from '../../../../atoms/support/DatosCliente';
import Integraciones from '../../../../atoms/support/Integraciones';
import classes from './Pendientes1.module.css';
import CargarMasButton from '../../../../atoms/support/CargarMasButton/CargarMasButton';
import PropTypes from 'prop-types';
import tablas from '../tablas.module.css';
import FinalPrice from '../../../../atoms/support/FinalPrice';
import useSupport from '../../../../../hooks/support/useSupport';
import { useSocketSupport } from '../../../../hooks/useSocketSupport';
import { useVerificarPermiso } from '../../../../hooks/useVerificarPermiso';

const Pendientes = ({ pendientes, online, conectarSocket, loading }) => {
    const {
        solicitarDriver,
        paginaActualPendientes,
        totalPaginasPendientes,
        permisoVerIntegraciones,
        obtenerTodasOrdenes,
        obtenerSiguienteEstado,
        cambiarEstado,
        cancelarOrden,
        cargarMas,
        totalPendientes,
        ordenSeleccionada,
        verOrdenModal,
        revertirPago,
        revertirCancelacion,
        imprimirOrden,
        isVerOrdenModalVisible,
    } = useSupport();
    const { esUsuarioComercio } = useVerificarPermiso();
    const mixpanel = useMixpanel();

    const { playAudio } = useSocketSupport();

    //Se trackea el tiempo en esta pantalla antes de solicitar driver
    useEffect(() => {
        mixpanel.time_event('request_driver');
        mixpanel.track('click_on_pending_tab');
    }, [mixpanel]);

    useEffect(() => {
        let intervalId;

        const playAudioIfPending = () => {
            // Comprobamos que hay pedidos pendientes, que el modal no está visible,
            // que el usuario es un comercio y que la orden seleccionada está actualizada
            if (
                totalPendientes > 0 &&
                !isVerOrdenModalVisible &&
                esUsuarioComercio() &&
                !ordenSeleccionada?.updated
            ) {
                playAudio();
            }
        };

        // Se inicia el intervalo cada 2 segundos
        intervalId = setInterval(playAudioIfPending, 2000);

        // Se limpia el intervalo cuando el componente se desmonta o cuando cambia totalPendientes o isVerOrdenModalVisible
        return () => {
            clearInterval(intervalId);
        };
    }, [totalPendientes, isVerOrdenModalVisible, ordenSeleccionada?.updated]);

    const obtenerOrdenesHandler = () => {
        obtenerTodasOrdenes();
        conectarSocket();
    };

    const cargarMasButtonHandler = useCallback(() => {
        cargarMas(3, paginaActualPendientes, totalPaginasPendientes);
    }, [cargarMas, paginaActualPendientes, totalPaginasPendientes]);

    const columnaIntegraciones = useMemo(
        () =>
            permisoVerIntegraciones
                ? {
                      dataIndex: 'integraciones',
                      key: 'integraciones',
                      render: (_text, record) => (
                          <Integraciones
                              jediId={record?.jediId}
                              fechaJedi={record?.fechaJEdi}
                              idMcDonalds={record?.idMcDonalds}
                              ordenSeleccionada={record?.ordenOriginal}
                              esIntegracion={record?.hasIntegration}
                              esJedi={record?.hasJedi}
                              estadoOrden={record?.estadoOrden}
                              idIntegracion={record?.idIntegracion}
                              envioIntegracion={record?.envioIntegracion}
                          />
                      ),
                  }
                : null,
        [permisoVerIntegraciones],
    );

    const columnasParte1 = useMemo(
        () => [
            {
                dataIndex: 'nombreCliente',
                key: 'nombreCliente',
                render: (_text, record) => (
                    <>
                        <Local
                            nombreLocal={record?.nombreLocal}
                            tipoEntrega={record?.tipoEntrega}
                            codigoOnline={record?.codigoPagoOnline}
                            telefonoLocal={record?.telefonoLocal}
                            tienePermiso={record?.canSeeOnlinePaymentType}
                        />
                        <div>
                            <strong> Orden ID: </strong>
                            {record?.ordenOriginal?.id}
                        </div>
                        <DatosCliente
                            nombre={record?.nombreCliente}
                            level={record?.level}
                            id={record?.idCliente}
                            razonSocial={record?.razonSocial}
                            RUC={record?.ruc}
                        />
                    </>
                ),
            },

            {
                dataIndex: 'precioFinal',
                key: 'precioFinal',
                render: (_text, record) => (
                    <>
                        <div className={classes.precioFinal}>
                            <Local
                                tipoPago={record?.tipoPago}
                                codigoOnline={record?.codigoPagoOnline}
                            />
                            <strong>
                                <FinalPrice
                                    ordenOriginal={record?.ordenOriginal}
                                />
                            </strong>
                        </div>
                    </>
                ),
            },
            {
                dataIndex: 'tiempoPedido',
                key: 'tiempoPedido',
                render: (_text, record) => (
                    <TiempoPedido
                        fechaPedido={record?.fechaPedido}
                        tiempoEstimado={record?.tiempoEstimado}
                        ordenOriginal={record?.ordenOriginal}
                    />
                ),
            },
        ],
        [],
    );

    const columnasParte2 = useMemo(
        () => [
            {
                dataIndex: 'acciones',
                key: 'acciones',
                render: (_text, record) => (
                    <Acciones
                        ordenSeleccionada={record?.ordenOriginal}
                        tieneMonchisDriver={record?.tieneMonchisDriver}
                        permitirSolicitarDriver={
                            record?.permitirSolicitarDriver
                        }
                        solicitarDriver={solicitarDriver}
                        tipoPago={record?.tipoPago}
                        alertSent={record?.alertSent}
                        permitirRevertirPagoOnline={
                            record?.permitirRevertirPagoOnline
                        }
                        revertirPago={revertirPago}
                        permitirCancelarOrden={record?.permitirCancelarOrden}
                        cancelarOrden={cancelarOrden}
                        permitirCambiarEstado={record?.permitirCambiarEstado}
                        cambiarEstado={cambiarEstado}
                        permitirRevertirCancelacion={
                            record?.permitirRevertirCancelacion
                        }
                        revertirCancelacion={revertirCancelacion}
                        permitirImprimirOrden={record?.permitirImprimirOrden}
                        imprimirOrden={imprimirOrden}
                        verOrden={verOrdenModal}
                        tiempoEstimado={record?.fechaPedido}
                        permitirVerOrden={record?.permitirVerOrden}
                        obtenerSiguienteEstado={obtenerSiguienteEstado}
                        tipoPagoSoloCupon={record?.tipoPagoSoloCupon}
                        pagoRevertido={record?.pagoRevertido}
                    />
                ),
            },
        ],
        [
            solicitarDriver,
            revertirPago,
            cancelarOrden,
            cambiarEstado,
            revertirCancelacion,
            imprimirOrden,
            verOrdenModal,
            obtenerSiguienteEstado,
        ],
    );

    const columns = useMemo(
        () =>
            columnaIntegraciones
                ? [...columnasParte1, columnaIntegraciones, ...columnasParte2]
                : [...columnasParte1, ...columnasParte2],
        [columnaIntegraciones, columnasParte1, columnasParte2],
    );

    const classNameRow = (record) => {
        let clase;
        const ordenAtrasada = TiempoPedido({
            fechaPedido: record.fechaPedido,
            tiempoEstimado: record.tiempoEstimado,
            ordenOriginal: record.ordenOriginal,
            obtenerAtrasado: true,
        });
        if (ordenAtrasada) {
            clase = classes.rowClass;
        } else {
            clase = 'table-row-dark';
        }
        return clase;
    };
    const combinedRowClassName = (record, index) => {
        return `${tablas.tableRow} ${classNameRow(record, index)}`;
    };
    return (
        <Space direction="vertical" size="small" className={classes.space}>
            <UltimaActualizacion
                onClick={obtenerOrdenesHandler}
                online={online}
            />
            <Table
                columns={columns}
                pagination={false}
                dataSource={pendientes}
                showHeader={false}
                loading={loading}
                rowClassName={combinedRowClassName}
            />
            <CargarMasButton
                onCargarMas={cargarMasButtonHandler}
                loading={loading}
                paginaActual={paginaActualPendientes}
                totalPaginas={totalPaginasPendientes}
            />
        </Space>
    );
};

export default Pendientes;

Pendientes.propTypes = {
    pendientes: PropTypes.array,
    online: PropTypes.bool,
    conectarSocket: PropTypes.func,
    loading: PropTypes.bool,
    alertSent: PropTypes.bool,
};
